import React, { useState, useEffect, useRef } from 'react';
import { Check, Star, Minus, ChevronDown, Unlock, Sparkles } from 'lucide-react';
import subscriptionApi, { SubscriptionTier } from '../../services/subscriptionApi';
import { getDisplayDiscount, PromoCampaign } from '../../utils/promoPricing';

interface TierComparisonTableProps {
  selectedTierId?: number | null;
  currentTierId?: number | null;
  onTierSelect: (tier: SubscriptionTier) => void;
  billingCycle?: 'monthly' | 'annual';
  onBillingCycleChange?: (cycle: 'monthly' | 'annual') => void;
  showBillingToggle?: boolean;
  filterCodes?: string[];
  compact?: boolean;
  disabled?: boolean;
  campaign?: PromoCampaign | null;
  parityDiscountPct?: number | null;
  parityCountry?: string | null;
  api?: {
    getTiers: (filterCodes?: string[]) => Promise<SubscriptionTier[]>;
  };
}

const calculateSavingsPercent = (monthly: number, annual: number): number => {
  if (monthly <= 0 || annual <= 0) return 0;
  const yearlyAtMonthly = monthly * 12;
  const savings = ((yearlyAtMonthly - annual) / yearlyAtMonthly) * 100;
  return Math.round(savings);
};

const TierComparisonTable: React.FC<TierComparisonTableProps> = ({
  selectedTierId,
  currentTierId,
  onTierSelect,
  billingCycle = 'monthly',
  onBillingCycleChange,
  showBillingToggle = true,
  filterCodes = ['starter', 'growth', 'scale', 'enterprise'],
  compact = false,
  disabled = false,
  campaign = null,
  parityDiscountPct = null,
  parityCountry = null,
  api,
}) => {
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const loadedRef = useRef(false);
  const filterCodesRef = useRef(filterCodes.join(','));

  // Access code state
  const [accessCode, setAccessCode] = useState('');
  const [accessCodeValidated, setAccessCodeValidated] = useState(false);
  const [accessCodeError, setAccessCodeError] = useState<string | null>(null);
  const [unlockedTier, setUnlockedTier] = useState<SubscriptionTier | null>(null);
  const [showAccessCodeInput, setShowAccessCodeInput] = useState(false);
  const [accessCodeFocused, setAccessCodeFocused] = useState(false);

  useEffect(() => {
    const currentFilterCodes = filterCodes.join(',');
    if (!loadedRef.current || filterCodesRef.current !== currentFilterCodes) {
      filterCodesRef.current = currentFilterCodes;
      loadedRef.current = true;
      loadTiers();
    }
  }, [filterCodes]);

  const loadTiers = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedTiers = api ? await api.getTiers(filterCodes) : await subscriptionApi.getTiers(filterCodes);
      setTiers(fetchedTiers);
    } catch (err: any) {
      setError('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  // Compute display tiers
  const displayTiers = React.useMemo(() => {
    let result = tiers;

    if (unlockedTier) {
      const alreadyInList = tiers.some((t) => t.id === unlockedTier.id);
      if (!alreadyInList) {
        result = [unlockedTier, ...tiers];
      }
      result = result.filter((t) => t.tier_code !== 'enterprise');
    }

    return result;
  }, [tiers, unlockedTier]);

  const avgSavingsPercent = React.useMemo(() => {
    const tiersWithPrices = displayTiers.filter(
      (t) => !t.is_contact_sales && parseFloat(t.price_monthly) > 0 && parseFloat(t.price_annual) > 0
    );
    if (tiersWithPrices.length === 0) return 20;
    const totalSavings = tiersWithPrices.reduce(
      (sum, t) => sum + calculateSavingsPercent(parseFloat(t.price_monthly), parseFloat(t.price_annual)),
      0
    );
    return Math.round(totalSavings / tiersWithPrices.length);
  }, [displayTiers]);

  // Access code handlers
  const validateAccessCode = async (code: string) => {
    if (!code.trim()) {
      setAccessCodeValidated(false);
      setAccessCodeError(null);
      setUnlockedTier(null);
      return;
    }

    try {
      const response = await fetch('/api/tier-access/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ code: code.toUpperCase() }),
      });
      const data = await response.json();

      if (data.valid && data.accessCode) {
        setAccessCodeValidated(true);
        setAccessCodeError(null);
        setUnlockedTier(data.accessCode.tier);
        onTierSelect(data.accessCode.tier);
      } else {
        setAccessCodeValidated(false);
        setAccessCodeError(data.error || 'Invalid access code');
        setUnlockedTier(null);
      }
    } catch (err) {
      setAccessCodeError('Failed to validate code');
      setAccessCodeValidated(false);
    }
  };

  const handleAccessCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setAccessCode(value);
    setAccessCodeValidated(false);
    setAccessCodeError(null);
  };

  const handleAccessCodeApply = () => {
    if (accessCode.trim()) {
      validateAccessCode(accessCode);
    }
  };

  const handleBillingCycleChange = (newCycle: 'monthly' | 'annual') => {
    if (onBillingCycleChange) {
      onBillingCycleChange(newCycle);
    }
  };

  const discFor = (tier: SubscriptionTier) =>
    getDisplayDiscount({ billingCycle, tierCode: tier.tier_code, parityPct: parityDiscountPct, parityCountry, campaign });

  const formatPrice = (tier: SubscriptionTier, undiscounted = false) => {
    const list = billingCycle === 'monthly' ? parseFloat(tier.price_monthly) : parseFloat(tier.price_annual);
    if (list === 0) return 'Free';
    if (tier.is_contact_sales) return 'Custom';
    const price = undiscounted ? list : list * discFor(tier).factor;
    if (billingCycle === 'annual') return `$${Math.round(price / 12)}/mo`;
    return `$${Math.round(price)}/mo`;
  };

  const getAnnualSavings = (tier: SubscriptionTier) => {
    const monthly = parseFloat(tier.price_monthly);
    const annual = parseFloat(tier.price_annual);
    if (monthly === 0 || annual === 0) return null;
    const savings = monthly * 12 - annual;
    if (savings <= 0) return null;
    return Math.round(savings);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '48px' }}>
        <div
          style={{
            width: '28px',
            height: '28px',
            border: '2px solid var(--v2-border)',
            borderTopColor: 'var(--v2-primary)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          backgroundColor: 'var(--v2-error-bg)',
          border: '1px solid #fecaca',
          borderRadius: 'var(--radius-lg)',
          padding: '16px',
          color: '#991b1b',
          fontSize: '0.875rem',
        }}
      >
        {error}
      </div>
    );
  }

  if (displayTiers.length === 0) {
    return (
      <div
        style={{
          backgroundColor: 'var(--v2-primary-light)',
          border: '1px solid #c7d2fe',
          borderRadius: 'var(--radius-lg)',
          padding: '16px',
          color: 'var(--v2-primary)',
          fontSize: '0.875rem',
        }}
      >
        No plans available
      </div>
    );
  }

  // Table cell padding based on compact mode
  const cellPadding = compact ? '12px 16px' : '14px 20px';

  return (
    <div>
      {/* Access Code Section */}
      <div style={{ marginBottom: '24px' }}>
        {!showAccessCodeInput ? (
          <div style={{ textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => setShowAccessCodeInput(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: 'none',
                border: 'none',
                color: 'var(--v2-primary)',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                padding: 0,
              }}
            >
              <Unlock size={14} strokeWidth={1.5} />
              Have an access code?
            </button>
          </div>
        ) : (
          <div
            style={{
              backgroundColor: 'var(--v2-surface-alt)',
              border: '1px solid var(--v2-border)',
              borderRadius: 'var(--radius-md)',
              padding: '16px',
            }}
          >
            <label
              style={{
                display: 'block',
                fontSize: '0.6875rem',
                fontWeight: 500,
                color: 'var(--v2-text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '8px',
              }}
            >
              Access Code
            </label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  value={accessCode}
                  onChange={handleAccessCodeChange}
                  onFocus={() => setAccessCodeFocused(true)}
                  onBlur={() => setAccessCodeFocused(false)}
                  placeholder="Enter code"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    backgroundColor: 'var(--v2-surface)',
                    border: `1px solid ${accessCodeError ? 'var(--v2-error)' : accessCodeValidated ? 'var(--v2-success)' : accessCodeFocused ? 'var(--v2-primary)' : 'var(--v2-border)'}`,
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '0.875rem',
                    fontFamily: 'var(--font-mono)',
                    color: 'var(--v2-text)',
                    outline: 'none',
                    boxSizing: 'border-box',
                    boxShadow: accessCodeFocused ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
                    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                  }}
                />
                {(accessCodeError || accessCodeValidated) && (
                  <p
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '0.8125rem',
                      color: accessCodeError ? 'var(--v2-error)' : 'var(--v2-success-dark)',
                      margin: '6px 0 0 0',
                    }}
                  >
                    {accessCodeValidated && <Check size={14} strokeWidth={2} />}
                    {accessCodeError || `Unlocked: ${unlockedTier?.tier_name}`}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={handleAccessCodeApply}
                disabled={!accessCode.trim() || accessCodeValidated}
                style={{
                  padding: '10px 16px',
                  backgroundColor: !accessCode.trim() || accessCodeValidated ? 'var(--v2-border)' : 'var(--v2-surface)',
                  color: !accessCode.trim() || accessCodeValidated ? 'var(--v2-text-muted)' : 'var(--v2-text)',
                  border: '1px solid var(--v2-border)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: !accessCode.trim() || accessCodeValidated ? 'not-allowed' : 'pointer',
                }}
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Unlocked Tier Notice */}
      {unlockedTier && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            backgroundColor: 'var(--v2-success-bg)',
            border: '1px solid #86efac',
            borderRadius: 'var(--radius-md)',
            padding: '12px 16px',
            marginBottom: '24px',
          }}
        >
          <Sparkles size={16} strokeWidth={1.5} color="var(--v2-success-dark)" />
          <p style={{ fontSize: '0.875rem', color: 'var(--v2-success-dark)', margin: 0 }}>
            Special plan unlocked: <strong style={{ fontWeight: 600 }}>{unlockedTier.tier_name}</strong>
          </p>
        </div>
      )}

      {/* Billing Toggle */}
      {showBillingToggle && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
          <div
            style={{
              display: 'inline-flex',
              backgroundColor: 'var(--v2-surface-alt)',
              borderRadius: 'var(--radius-md)',
              padding: '4px',
              gap: '4px',
              border: '1px solid var(--v2-border)',
            }}
          >
            <button
              onClick={() => handleBillingCycleChange('monthly')}
              style={{
                padding: '10px 20px',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: billingCycle === 'monthly' ? '#ffffff' : 'var(--v2-text-tertiary)',
                backgroundColor: billingCycle === 'monthly' ? 'var(--v2-primary)' : 'transparent',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              Monthly
            </button>
            <button
              onClick={() => handleBillingCycleChange('annual')}
              style={{
                padding: '10px 20px',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: billingCycle === 'annual' ? '#ffffff' : 'var(--v2-text-tertiary)',
                backgroundColor: billingCycle === 'annual' ? 'var(--v2-primary)' : 'transparent',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              Annual
              <span
                style={{
                  backgroundColor: billingCycle === 'annual' ? 'rgba(255,255,255,0.2)' : 'var(--v2-success-bg)',
                  color: billingCycle === 'annual' ? '#ffffff' : 'var(--v2-success-dark)',
                  padding: '2px 8px',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                }}
              >
                SAVE {avgSavingsPercent}%
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div
        style={{
          border: '1px solid var(--v2-border)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
          backgroundColor: 'var(--v2-surface)',
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: compact ? '0.8125rem' : '0.875rem',
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  fontWeight: 500,
                  width: '25%',
                  padding: cellPadding,
                  textAlign: 'left',
                  color: 'var(--v2-text-muted)',
                  borderBottom: '1px solid var(--v2-border)',
                  backgroundColor: 'var(--v2-surface-alt)',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                }}
              />
              {displayTiers.map((tier) => {
                const isCurrent = currentTierId === tier.id;
                const isUnlocked = unlockedTier?.id === tier.id;
                return (
                  <th
                    key={tier.id}
                    style={{
                      fontWeight: 600,
                      padding: cellPadding,
                      textAlign: 'center',
                      color: 'var(--v2-text)',
                      backgroundColor: isCurrent ? 'var(--v2-primary-light)' : tier.is_popular ? 'rgba(79, 70, 229, 0.03)' : 'var(--v2-surface-alt)',
                      borderLeft: '1px solid var(--v2-border)',
                      borderBottom: '1px solid var(--v2-border)',
                      position: 'relative',
                    }}
                  >
                    {/* Unlocked Badge */}
                    {isUnlocked && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
                          color: 'white',
                          fontSize: '0.5625rem',
                          fontWeight: 700,
                          padding: '3px 10px',
                          borderBottomLeftRadius: 'var(--radius-sm)',
                          borderBottomRightRadius: 'var(--radius-sm)',
                          whiteSpace: 'nowrap',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '3px',
                        }}
                      >
                        <Check size={8} strokeWidth={3} />
                        UNLOCKED
                      </div>
                    )}
                    {/* Popular Badge */}
                    {tier.is_popular && !isUnlocked && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)',
                          color: 'white',
                          fontSize: '0.5625rem',
                          fontWeight: 700,
                          padding: '3px 10px',
                          borderBottomLeftRadius: 'var(--radius-sm)',
                          borderBottomRightRadius: 'var(--radius-sm)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        POPULAR
                      </div>
                    )}
                    <div style={{ paddingTop: tier.is_popular || isUnlocked ? '14px' : 0 }}>
                      <div style={{ fontSize: compact ? '0.9375rem' : '1rem', fontWeight: 600 }}>{tier.tier_name}</div>
                      {isCurrent && (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginTop: '4px',
                            fontSize: '0.625rem',
                            fontWeight: 600,
                            color: 'var(--v2-primary)',
                            backgroundColor: 'var(--v2-primary-light)',
                            padding: '2px 8px',
                            borderRadius: 'var(--radius-sm)',
                          }}
                        >
                          <Star size={10} strokeWidth={2} />
                          Current
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {/* Price Row */}
            <tr
              style={{ backgroundColor: hoveredRow === 'price' ? 'var(--v2-surface-hover)' : undefined }}
              onMouseEnter={() => setHoveredRow('price')}
              onMouseLeave={() => setHoveredRow(null)}
            >
              <td
                style={{
                  fontWeight: 500,
                  padding: cellPadding,
                  color: 'var(--v2-text-secondary)',
                  borderBottom: '1px solid var(--v2-border-light)',
                }}
              >
                {billingCycle === 'monthly' ? 'Monthly Price' : 'Annual Price'}
              </td>
              {displayTiers.map((tier) => {
                const isCurrent = currentTierId === tier.id;
                return (
                  <td
                    key={tier.id}
                    style={{
                      padding: cellPadding,
                      textAlign: 'center',
                      backgroundColor: isCurrent ? 'rgba(79, 70, 229, 0.04)' : tier.is_popular ? 'rgba(79, 70, 229, 0.02)' : undefined,
                      borderLeft: '1px solid var(--v2-border-light)',
                      borderBottom: '1px solid var(--v2-border-light)',
                    }}
                  >
                    {!tier.is_contact_sales && discFor(tier).factor < 1 && (
                      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--v2-text-tertiary)', textDecoration: 'line-through' }}>{formatPrice(tier, true)}</div>
                    )}
                    <div style={{ fontSize: '1.375rem', fontWeight: 700, color: 'var(--v2-primary)' }}>{formatPrice(tier)}</div>
                    {!tier.is_contact_sales && (discFor(tier).badge || discFor(tier).hint) && (
                      <div style={{ marginTop: '4px', fontSize: '0.6875rem', fontWeight: 600, color: discFor(tier).kind === 'parity' ? '#0e7490' : '#FF5847' }}>
                        {discFor(tier).badge || discFor(tier).hint}
                      </div>
                    )}
                    {billingCycle === 'annual' && getAnnualSavings(tier) && (
                      <span
                        style={{
                          display: 'inline-block',
                          marginTop: '4px',
                          backgroundColor: 'var(--v2-success-bg)',
                          color: 'var(--v2-success-dark)',
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: '0.6875rem',
                          fontWeight: 600,
                        }}
                      >
                        Save ${getAnnualSavings(tier)}/yr
                      </span>
                    )}
                    {billingCycle === 'annual' && !tier.is_contact_sales && tier.price_annual && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--v2-text-muted)', marginTop: '4px' }}>
                        {discFor(tier).kind === 'campaign'
                          ? `$${Math.round(parseFloat(tier.price_annual) * discFor(tier).factor)} first year, then $${tier.price_annual}/yr`
                          : `$${Math.round(parseFloat(tier.price_annual) * discFor(tier).factor)} billed annually`}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>

            {/* Monthly Active Users Row */}
            <tr
              style={{ backgroundColor: hoveredRow === 'users' ? 'var(--v2-surface-hover)' : undefined }}
              onMouseEnter={() => setHoveredRow('users')}
              onMouseLeave={() => setHoveredRow(null)}
            >
              <td
                style={{
                  fontWeight: 500,
                  padding: cellPadding,
                  color: 'var(--v2-text-secondary)',
                  borderBottom: '1px solid var(--v2-border-light)',
                }}
              >
                Monthly Active Users
              </td>
              {displayTiers.map((tier) => {
                const isCurrent = currentTierId === tier.id;
                return (
                  <td
                    key={tier.id}
                    style={{
                      padding: cellPadding,
                      textAlign: 'center',
                      fontWeight: 600,
                      color: 'var(--v2-text)',
                      backgroundColor: isCurrent ? 'rgba(79, 70, 229, 0.04)' : tier.is_popular ? 'rgba(79, 70, 229, 0.02)' : undefined,
                      borderLeft: '1px solid var(--v2-border-light)',
                      borderBottom: '1px solid var(--v2-border-light)',
                    }}
                  >
                    {tier.base_named_users ?? 'Custom'}
                  </td>
                );
              })}
            </tr>

            {/* Public View Users Row */}
            <tr
              style={{ backgroundColor: hoveredRow === 'public' ? 'var(--v2-surface-hover)' : undefined }}
              onMouseEnter={() => setHoveredRow('public')}
              onMouseLeave={() => setHoveredRow(null)}
            >
              <td
                style={{
                  fontWeight: 500,
                  padding: cellPadding,
                  color: 'var(--v2-text-secondary)',
                  borderBottom: '1px solid var(--v2-border-light)',
                }}
              >
                Public View Licenses
              </td>
              {displayTiers.map((tier) => {
                const isCurrent = currentTierId === tier.id;
                return (
                  <td
                    key={tier.id}
                    style={{
                      padding: cellPadding,
                      textAlign: 'center',
                      fontWeight: 600,
                      color: 'var(--v2-text)',
                      backgroundColor: isCurrent ? 'rgba(79, 70, 229, 0.04)' : tier.is_popular ? 'rgba(79, 70, 229, 0.02)' : undefined,
                      borderLeft: '1px solid var(--v2-border-light)',
                      borderBottom: '1px solid var(--v2-border-light)',
                    }}
                  >
                    {tier.base_public_view_users ?? 'Custom'}
                  </td>
                );
              })}
            </tr>

            {/* Key Features (if not compact) */}
            {!compact &&
              displayTiers[0]?.features?.slice(0, 3).map((_, featureIndex) => (
                <tr
                  key={featureIndex}
                  style={{ backgroundColor: hoveredRow === `feature-${featureIndex}` ? 'var(--v2-surface-hover)' : undefined }}
                  onMouseEnter={() => setHoveredRow(`feature-${featureIndex}`)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  <td
                    style={{
                      fontWeight: 400,
                      padding: cellPadding,
                      color: 'var(--v2-text-secondary)',
                      borderBottom: '1px solid var(--v2-border-light)',
                    }}
                  >
                    {displayTiers[0]?.features[featureIndex] || ''}
                  </td>
                  {displayTiers.map((tier) => {
                    const isCurrent = currentTierId === tier.id;
                    return (
                      <td
                        key={tier.id}
                        style={{
                          padding: cellPadding,
                          textAlign: 'center',
                          backgroundColor: isCurrent ? 'rgba(79, 70, 229, 0.04)' : tier.is_popular ? 'rgba(79, 70, 229, 0.02)' : undefined,
                          borderLeft: '1px solid var(--v2-border-light)',
                          borderBottom: '1px solid var(--v2-border-light)',
                        }}
                      >
                        {tier.features[featureIndex] ? (
                          <span
                            style={{
                              width: '22px',
                              height: '22px',
                              borderRadius: '50%',
                              backgroundColor: 'var(--v2-success-bg)',
                              color: 'var(--v2-success-dark)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Check size={14} strokeWidth={2.5} />
                          </span>
                        ) : (
                          <Minus size={18} strokeWidth={1.5} color="var(--v2-text-muted)" />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}

            {/* Action Row */}
            <tr>
              <td style={{ padding: cellPadding, borderBottom: 'none' }} />
              {displayTiers.map((tier) => {
                const isCurrent = currentTierId === tier.id;
                const isSelected = selectedTierId === tier.id;
                return (
                  <td
                    key={tier.id}
                    style={{
                      padding: cellPadding,
                      textAlign: 'center',
                      backgroundColor: isCurrent ? 'rgba(79, 70, 229, 0.04)' : tier.is_popular ? 'rgba(79, 70, 229, 0.02)' : undefined,
                      borderLeft: '1px solid var(--v2-border-light)',
                      borderBottom: 'none',
                    }}
                  >
                    {tier.is_contact_sales ? (
                      <a
                        href="mailto:sales@dashboardfox.com"
                        style={{
                          display: 'inline-block',
                          padding: '10px 20px',
                          backgroundColor: 'var(--v2-surface)',
                          color: 'var(--v2-text)',
                          border: '1px solid var(--v2-border)',
                          borderRadius: 'var(--radius-md)',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          textDecoration: 'none',
                          opacity: disabled ? 0.5 : 1,
                          pointerEvents: disabled ? 'none' : 'auto',
                        }}
                      >
                        Contact Sales
                      </a>
                    ) : isCurrent ? (
                      <button
                        disabled
                        title="This is your current plan"
                        style={{
                          padding: '10px 20px',
                          backgroundColor: 'var(--v2-border)',
                          color: 'var(--v2-text-muted)',
                          border: 'none',
                          borderRadius: 'var(--radius-md)',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          cursor: 'not-allowed',
                        }}
                      >
                        Current Plan
                      </button>
                    ) : (
                      <button
                        onClick={() => onTierSelect(tier)}
                        disabled={disabled}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          padding: '10px 20px',
                          backgroundColor: isSelected ? 'var(--v2-primary)' : 'var(--v2-surface)',
                          color: isSelected ? '#ffffff' : 'var(--v2-text)',
                          border: isSelected ? 'none' : '1px solid var(--v2-border)',
                          borderRadius: 'var(--radius-md)',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          opacity: disabled ? 0.5 : 1,
                          transition: 'all 0.15s',
                        }}
                      >
                        {isSelected && <Check size={16} strokeWidth={2.5} />}
                        {isSelected ? 'Selected' : 'Select'}
                      </button>
                    )}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TierComparisonTable;
